import { useEffect, useRef, useState } from 'react';
import { BordPhoto } from 'bord-design-system';
import { BordOneToneIcon } from 'components/NudosComponents';
import NudosSearchBar from 'components/NudosComponents/NudosSearchBar/NudosSearchBar';
import { IQuoteResponsibleUser } from 'types/quote.types';
import { IbackofficeUserData } from 'types/configuration.types';
import useUsersState from 'state/useUsersState';
import { QuoteService } from 'services/quote.service';
import './ResponsiblesDropdown.scss';

interface IResponsiblesDropdown {
  responsibles: IQuoteResponsibleUser[];
  quoteId?: number;
  onResponsiblesUpdate?: () => void;
}

/**
 * Dropdown de búsqueda para asignar o desasignar responsables de una cotización.
 * Muestra "Desasignar" para usuarios ya asignados y "Asignar" para los disponibles.
 */
const ResponsiblesDropdown = ({ responsibles, quoteId, onResponsiblesUpdate }: IResponsiblesDropdown) => {
  const { users } = useUsersState();
  const [searchText, setSearchText] = useState('');
  const [loadingUserId, setLoadingUserId] = useState<number | undefined>();
  const listRef = useRef<HTMLDivElement>(null);
  const savedScrollRef = useRef(0);

  const assignedIds = new Set(responsibles.map(r => r.userId));

  const filteredUsers = users
    .filter(
      (user: IbackofficeUserData) =>
        user.firstName?.toLowerCase().includes(searchText.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchText.toLowerCase())
    )
    .sort((a, b) => Number(assignedIds.has(b.id)) - Number(assignedIds.has(a.id)));

  /** Restaura la posición de scroll de la lista cuando los responsables cambian */
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = savedScrollRef.current;
  }, [responsibles]);

  /** Asigna un usuario como responsable de la cotización */
  const handleAssign = async (userId: number) => {
    if (!quoteId) return;
    savedScrollRef.current = listRef.current?.scrollTop ?? 0;
    setLoadingUserId(userId);
    try {
      await QuoteService.assignResponsible(quoteId, userId);
      onResponsiblesUpdate?.();
    } finally {
      setLoadingUserId(undefined);
    }
  };

  /** Desasigna a un responsable usando el id del registro pivot */
  const handleUnassign = async (userId: number) => {
    const record = responsibles.find(r => r.userId === userId);
    if (!record) return;
    savedScrollRef.current = listRef.current?.scrollTop ?? 0;
    setLoadingUserId(userId);
    try {
      await QuoteService.unassignResponsible(record.id);
      onResponsiblesUpdate?.();
    } finally {
      setLoadingUserId(undefined);
    }
  };

  return (
    <div className="responsiblesDropdown">
      <NudosSearchBar
        placeholder="Busca por nombre o correo"
        handleChange={setSearchText}
        customClassName="dropdownSearchBar"
        includeMagnifierIcon
        customInputClass="customInputClass"
      />
      <div className="userList" ref={listRef}>
        {filteredUsers.map(user => {
          const isAssigned = assignedIds.has(user.id);
          const isLoading = loadingUserId === user.id;
          const iconStroke = isLoading ? '#FFFFFF' : 'currentColor';
          return (
            <div key={user.id} className="userRow">
              <div className="userInfo">
                <BordPhoto
                  url={user?.photoProfile}
                  variant="circle"
                  size="s20"
                  avatarProps={{ variant: 'circularUser' }}
                  imageProps={{ style: { width: '100%', height: '100%', objectFit: 'cover' } }}
                />
                <div className="userDetails">
                  <div className="userName">
                    {user.firstName} {user.lastName}
                  </div>
                  <div className="userEmail">{user.email}</div>
                </div>
              </div>
              <button
                className={`actionButton${isAssigned ? ' assigned' : ''}`}
                disabled={isLoading || !quoteId}
                onClick={() => isAssigned ? handleUnassign(user.id) : handleAssign(user.id)}
              >
                {isLoading ? (
                  <BordOneToneIcon variant="loader" standardSize={14} stroke={iconStroke} />
                ) : (
                  <>
                    {isAssigned ? 'Desasignar' : 'Asignar'}
                    <BordOneToneIcon
                      variant={isAssigned ? 'minusCircle' : 'plusCircle'}
                      standardSize={14}
                      stroke={iconStroke}
                    />
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ResponsiblesDropdown;
